import React, { useEffect, useState } from "react";
import { Truck, Loader2, Plus, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useFormatEUR } from "@/lib/useFormatEUR";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

/* Extern werk (werk van derden): kost derde + haal-/brengservice → één bedrag voor de klant; arbeid apart. */
export const ExternalWorkDialog = ({ open, onOpenChange, settings, onAdd }) => {
  const { t } = useLang();
  const fm = useFormatEUR();
  const [desc, setDesc] = useState("");
  const [supplier, setSupplier] = useState("");
  const [cost, setCost] = useState("");
  const [costIncl, setCostIncl] = useState(false);
  const [transport, setTransport] = useState(String(settings?.external_transport_fee ?? 75));
  const [withTransport, setWithTransport] = useState(true);
  const [official, setOfficial] = useState(false);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const n = Number(cost);
    if (!open || !(n > 0)) { setRes(null); return; }
    const net = Math.round((costIncl ? n / 1.21 : n) * 100) / 100;
    const tr = withTransport ? Number(transport) || 0 : 0;
    if (official) {
      setRes({ net, direct: tr, external_rate: 0, external_markup: 0, advice: Math.round((net + tr) * 100) / 100, official: true });
      return;
    }
    const h = setTimeout(() => {
      setBusy(true);
      api.post("/pricing/calculate", { net, direct: tr, category_code: "C71" }).then((r) => setRes(r.data)).catch(() => setRes(null)).finally(() => setBusy(false));
    }, 350);
    return () => clearTimeout(h);
  }, [open, cost, costIncl, transport, withTransport, official]);

  const add = () => {
    if (!desc.trim()) return toast.error(t("serviceNameRequired"));
    if (!res?.advice) return toast.error(t("externalNeedCost"));
    const tag = official ? t("officialCostTag") : t("externalWorkPrefix");
    const name = `${tag} – ${desc.trim()}${supplier.trim() ? ` (${supplier.trim()})` : ""}${withTransport && Number(transport) > 0 ? ` · ${t("inclBringService")}` : ""}`;
    onAdd({ name, quantity: 1, unit_price: Number(res.advice), external: true, official_cost: official, cost_price: Number(res.net), transport_fee: Number(res.direct), supplier_name: supplier.trim(), pricing_snapshot: res });
    onOpenChange(false); setDesc(""); setSupplier(""); setCost(""); setRes(null); setOfficial(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Truck className="h-5 w-5 text-primary" />{t("externalWork")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>{t("externalDesc")}</Label><Input autoFocus value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={t("externalDescPh")} data-testid="ext-desc" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label>{t("externalSupplier")}</Label><Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="bv. DPF Cleaning BV" data-testid="ext-supplier" /></div>
            <div className="space-y-1.5"><Label>{t("externalCost")}</Label>
              <div className="flex gap-1"><Input type="number" min="0" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} dir="ltr" data-testid="ext-cost" />
                <button type="button" onClick={() => setCostIncl((v) => !v)} className="shrink-0 px-2 rounded-md border border-border text-[11px] font-mono" data-testid="ext-cost-vat">{costIncl ? "incl" : "excl"}</button></div></div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="ext-transport-toggle">
            <input type="checkbox" checked={withTransport} onChange={(e) => setWithTransport(e.target.checked)} className="h-4 w-4 accent-primary" />{t("bringService")}
            <Input type="number" min="0" step="0.5" value={transport} onChange={(e) => setTransport(e.target.value)} disabled={!withTransport} className="h-8 w-24 ml-auto" dir="ltr" data-testid="ext-transport" />
          </label>
          <label className={`flex items-start gap-2 text-sm cursor-pointer rounded-lg border p-2.5 ${official ? "border-emerald-500/50 bg-emerald-500/5" : "border-border"}`} data-testid="ext-official-toggle">
            <input type="checkbox" checked={official} onChange={(e) => setOfficial(e.target.checked)} className="h-4 w-4 mt-0.5 accent-emerald-600" />
            <span><span className="font-medium">{t("officialCostLabel")}</span><br /><span className="text-[11px] text-muted-foreground">{t("officialCostHint")}</span></span>
          </label>
          {res?.advice && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm space-y-1" data-testid="ext-result">
              <div className="flex justify-between"><span>{t("externalCostLine")}</span><span className="font-mono">{fm(res.net)}</span></div>
              {official
                ? <div className="flex justify-between text-emerald-700"><span>{t("officialNoMarkup")}</span><span className="font-mono">{fm(0)}</span></div>
                : <div className="flex justify-between text-muted-foreground"><span>+ {t("externalMarkup")} {Math.round(Number(res.external_rate) * 100)} %</span><span className="font-mono">{fm(res.external_markup)}</span></div>}
              {Number(res.direct) > 0 && <div className="flex justify-between text-muted-foreground"><span>+ {t("bringService")}</span><span className="font-mono">{fm(res.direct)}</span></div>}
              <div className="flex justify-between font-bold border-t border-border pt-1"><span>{t("externalCustomerAmount")}</span><span className="font-mono" data-testid="ext-advice">{fm(res.advice)} <span className="text-xs text-muted-foreground font-normal">excl. · {fm(Number(res.advice) * 1.21)} incl.</span></span></div>
              <div className="text-[11px] text-muted-foreground flex gap-1.5 pt-1"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />{t("externalLabourHint")}</div>
            </div>
          )}
          {busy && <div className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />{t("loading")}</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={add} disabled={!res?.advice} className="rounded-full" data-testid="ext-add"><Plus className="h-4 w-4 mr-1" />{t("addToCard")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
